import type { PostHog } from 'posthog-js'

// Analytics de produto (PostHog). Sem VITE_POSTHOG_KEY (ex.: dev local) é um
// no-op total — nada é carregado nem enviado. Só arranca em produção.
//
// Objetivo: medir o funil do CLIENTE
//   visita -> registo -> primeira marcação -> primeira avaliação
// para saber onde os clientes desistem.
//
// 07/10/2026: o posthog-js (76KB comprimidos) deixou de ser carregado antes de
// a página aparecer — no telemóvel o primeiro conteúdo só surgia aos 5,5s.
// Agora carrega depois do evento 'load'; o que for registado antes fica numa
// fila e é enviado assim que estiver pronto.
//
// 23/10/2026: nada disto arranca sem a pessoa aceitar a medição no aviso de
// cookies (lib/consentimentoCookies.ts). Recusar depois desliga os dois.

let ligado = false
let iniciado = false
let posthog: PostHog | null = null
let fila: Array<(p: PostHog) => void> = []

function usar(fn: (p: PostHog) => void) {
  if (!ligado) return
  if (posthog) {
    try { fn(posthog) } catch { /* nunca quebrar a app por analytics */ }
  } else {
    fila.push(fn)
  }
}

// Remove tokens/segredos dos URLs capturados (OAuth devolve-os no #fragment).
// 't' é a chave secreta do acompanhamento do pedido (/pedido/<codigo>?t=...):
// quem a tem pode confirmar ou cancelar o pedido do cliente.
const PARAMS_SENSIVEIS = ['access_token', 'refresh_token', 'provider_token', 'provider_refresh_token', 'token', 'code', 'id_token', 't']
function scrubUrl(url: unknown): unknown {
  if (typeof url !== 'string') return url
  try {
    const u = new URL(url)
    u.hash = '' // OAuth do Supabase usa fragmento (#access_token=...)
    for (const p of PARAMS_SENSIVEIS) u.searchParams.delete(p)
    return u.toString()
  } catch {
    return url.split('#')[0]
  }
}
function scrubUrlProps(props: Record<string, unknown>): Record<string, unknown> {
  for (const chave of ['$current_url', '$referrer', '$pathname']) {
    if (props[chave]) props[chave] = scrubUrl(props[chave])
  }
  return props
}

// Google Analytics 4 (09/10/2026): tráfego e origem das visitas, ao lado do
// PostHog (funil). Carrega também depois do 'load'. As páginas vistas são
// enviadas só por paginaVista(), e o URL limpo fica fixo ('set') para TODOS os
// eventos (rolagens, cliques de saída…) — senão o GA lê o URL completo, com o
// ?t= do pedido. No painel, as páginas vistas pelo histórico estão desligadas.
const GA_ID = (import.meta.env.VITE_GA_ID as string | undefined) || 'G-20ESCX8QHL'
let gaLigado = false
type Gtag = (...args: unknown[]) => void
declare global {
  interface Window { dataLayer?: unknown[]; gtag?: Gtag }
}

// Só estes eventos vão para o GA (o resto fica no PostHog).
const EVENTOS_GA = new Set<string>([
  'pedido_iniciado', 'pedido_codigo_pedido', 'pedido_criado', 'pedido_erro', 'pedido_whatsapp', 'whatsapp_clicked',
])

function gtag(...args: unknown[]) {
  if (!gaLigado || !window.gtag) return
  try { window.gtag(...args) } catch { /* nunca quebrar a app por analytics */ }
}

function urlLimpo(): string {
  return String(scrubUrl(window.location.href))
}

function iniciarGoogleAnalytics() {
  if (!GA_ID || !import.meta.env.PROD) return
  window.dataLayer = window.dataLayer || []
  // O gtag.js exige o objeto 'arguments' original, não um array.
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function () { window.dataLayer!.push(arguments) } as Gtag
  gaLigado = true
  window.gtag('js', new Date())
  window.gtag('set', { page_location: urlLimpo() })
  // Até o script carregar, os comandos ficam na fila (dataLayer).
  window.gtag('config', GA_ID, { send_page_view: false })

  const carregar = () => {
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
    document.head.appendChild(s)
  }
  if (document.readyState === 'complete') setTimeout(carregar, 0)
  else window.addEventListener('load', () => setTimeout(carregar, 0), { once: true })
}

/** Página vista no GA (chamar a cada mudança de rota). */
export function paginaVista() {
  const page_location = urlLimpo()
  gtag('set', { page_location })
  gtag('event', 'page_view', {
    page_location,
    page_path: window.location.pathname,
    page_title: document.title,
  })
}

export function initAnalytics() {
  if (iniciado) {
    reativarAnalytics()
    return
  }
  iniciado = true
  iniciarGoogleAnalytics()

  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  if (!key || !import.meta.env.PROD) return
  ligado = true

  const carregar = () => {
    import('posthog-js')
      .then(({ default: ph }) => {
        ph.init(key, {
          api_host: (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://eu.i.posthog.com',
          capture_pageview: true,
          autocapture: false, // só eventos explícitos do funil — menos ruído e quota
          persistence: 'localStorage',
          // SEGURANÇA: o login Google devolve tokens no fragmento do URL
          // (#access_token=...&refresh_token=...). Sem isto, o pageview gravava
          // credenciais de sessão no PostHog. Removemos hash e params sensíveis.
          sanitize_properties: (props) => scrubUrlProps(props),
        })
        posthog = ph
        const pendentes = fila
        fila = []
        for (const fn of pendentes) {
          try { fn(ph) } catch { /* idem */ }
        }
      })
      .catch(() => { ligado = false; fila = [] })
  }
  if (document.readyState === 'complete') setTimeout(carregar, 0)
  else window.addEventListener('load', () => setTimeout(carregar, 0), { once: true })

  // Rastreio de erros no PostHog (substitui o Sentry).
  window.addEventListener('error', (e) => captureError(e.error ?? e.message))
  window.addEventListener('unhandledrejection', (e) => captureError(e.reason))
}

// Depois de "Recusar" e de novo "Aceitar" na mesma visita.
function reativarAnalytics() {
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = false
  gaLigado = !!window.gtag
  ligado = !!import.meta.env.VITE_POSTHOG_KEY && import.meta.env.PROD
  posthog?.opt_in_capturing()
}

/** Recusa da medição: para de enviar e apaga os cookies do Google Analytics. */
export function desligarAnalytics() {
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = true
  gaLigado = false
  posthog?.opt_out_capturing()
  ligado = false
  fila = []
  const dominio = window.location.hostname.replace(/^www\./, '')
  for (const c of document.cookie.split(';')) {
    const nome = c.split('=')[0].trim()
    if (nome === '_ga' || nome.startsWith('_ga_')) {
      for (const d of ['', `; domain=.${dominio}`]) document.cookie = `${nome}=; Max-Age=0; path=/${d}`
    }
  }
}

/** Reporta uma exceção ao PostHog Error Tracking (no-op sem chave). */
export function captureError(erro: unknown, extra?: Record<string, unknown>) {
  const e = erro instanceof Error ? erro : new Error(String(erro))
  usar((p) => p.captureException(e, extra))
}

/** Associa os eventos a este utilizador (chamar no login/registo). */
export function identifyUser(id: string, props?: Record<string, unknown>) {
  usar((p) => p.identify(id, props))
}

export function track(evento: string, props?: Record<string, unknown>) {
  usar((p) => p.capture(evento, props))
  if (EVENTOS_GA.has(evento)) gtag('event', evento, paramsGa(props))
}

// O GA só aceita valores simples; o código do pedido fica só no PostHog.
function paramsGa(props?: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(props ?? {})) {
    if (k === 'pedido_codigo') continue
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = v
  }
  return out
}

export function resetAnalytics() {
  usar((p) => p.reset())
}

// Nomes de eventos centralizados (evita divergência de strings).
export const EVENTOS = {
  registo: 'cliente_registado',
  agendamento: 'cliente_agendamento_criado',
  avaliacao: 'cliente_avaliacao_enviada',
  login: 'login_realizado',
  prestadorPerfil: 'prestador_perfil_visualizado',
  servicoSelecionado: 'servico_selecionado',
  favoritoAdicionado: 'favorito_adicionado',
  favoritoRemovido: 'favorito_removido',
  // Plano de eventos do funil do cliente (nomes em inglês, 2026-09-29).
  // Os eventos em português acima mantêm-se: os funis existentes no PostHog
  // dependem deles.
  userRegistered: 'user_registered',
  customerRegistered: 'customer_registered',
  searchStarted: 'search_started',
  categoryViewed: 'category_viewed',
  providerViewed: 'provider_viewed',
  serviceViewed: 'service_viewed',
  bookingStarted: 'booking_started',
  bookingCompleted: 'booking_completed',
  bookingCancelled: 'booking_cancelled',
  contactProvider: 'contact_provider',
  whatsappClicked: 'whatsapp_clicked',
  // Funil do Pedido (/pedir), o centro do produto (DEC-011) — 05/10/2026.
  // Até aqui não havia nenhum evento: 117 visitantes em 30 dias, 0 pedidos,
  // e só a BD (pedido_otp) mostrava que ninguém chegou a pedir o código.
  pedidoIniciado: 'pedido_iniciado',
  pedidoCodigoPedido: 'pedido_codigo_pedido',
  pedidoCriado: 'pedido_criado',
  pedidoErro: 'pedido_erro',
  // 06/10/2026: um visitante tocou em "Continuar" e nada ficou registado —
  // este evento sai antes de qualquer validação, para medir o toque em si.
  pedidoContinuar: 'pedido_continuar',
  pedidoWhatsapp: 'pedido_whatsapp',
} as const
